import React, { useState, useEffect, useMemo } from 'react';
import FullscreenBtn from './FullscreenBtn';
import './ROListView.css';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import * as subscriptions from '../graphql/subscriptions';
import AssignTechModal from './Modals/AssignTechModal';
import NoteModal from './Modals/NoteModal';
import ConfirmModal from './Modals/ConfirmModal';
import NewROModal from './Modals/NewROModal';
import SmartContextMenu from './SmartContextMenu';
import useOperationalStore, {
  PIPELINE,
  fmtDur as domainFmtDur,
  getActiveJobs,
  getJobElapsedMs,
  getJobHealth,
  getPipelineIndex,
  getStageAge,
  getStageLabel,
  groupJobsByStatus,
  isIdle as domainIsIdle,
} from '../platform/store/useOperationalStore';
import { createServiceSummaryJson } from '../platform/history/serviceRecord';
import { EVENT_TYPES, recordTruckEvent } from '../platform/events/operationEvents';
import { getCurrentRole, hasPermission, PERMISSIONS } from '../shared/permissions';

const client = generateClient();

function nowSeconds() {
  return Math.floor(Date.now() / 1000);
}

/** Status → pill variant (colors per owner mockup) */
const PILL_VARIANTS = {
  yard:         'checkedin',
  triage:       'diag',
  waiting:      'diag',
  approval:     'approval',
  'parts-auth': 'approval',
  parts:        'parts',
  'parts-hold': 'parts',
  wip:          'repair',
  'road-test':  'roadtest',
  qc:           'roadtest',
  done:         'ready',
};

/** "Updated" cell: date + time on two lines, like the mockup */
function fmtUpdated(lastStatusChange) {
  if (!lastStatusChange) return { date: '—', time: '' };
  const d = new Date(lastStatusChange * 1000);
  return {
    date: d.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
  };
}

export default function ROListView({
  isAddModalOpen,
  setIsAddModalOpen,
  onAddTruck,
  onCustomerCheckInsUpdate,
  onBoardStateChange,
}) {
  // ── Read trucks + loading from Zustand store ──────────────────────────────
  const allTrucks          = useOperationalStore(s => s.trucks);
  const storeLoading       = useOperationalStore(s => s.loading);
  const localUpdateTruck   = useOperationalStore(s => s.localUpdateTruck);
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const setActiveView      = useOperationalStore(s => s.setActiveView);

  // Filter to active statuses only (store holds all trucks including completed)
  const activeTrucks = useMemo(
    () => getActiveJobs(allTrucks),
    [allTrucks]
  );

  // ── Local UI state ────────────────────────────────────────────────────────
  const [customerCheckIns, setCustomerCheckIns] = useState([]);
  const [checkInsLoading, setCheckInsLoading]   = useState(true);
  const [contextMenu, setContextMenu]           = useState({ show: false, x: 0, y: 0 });
  const [selectedTruck, setSelectedTruck]       = useState(null);
  const [isAssignTechOpen, setIsAssignTechOpen] = useState(false);
  const [isNoteOpen, setIsNoteOpen]             = useState(false);
  const [isEditOpen, setIsEditOpen]             = useState(false);
  const [confirmModal, setConfirmModal]         = useState({ isOpen: false, message: '', onConfirm: null, onCancel: null, requireLaborHours: false });
  const [filterStage, setFilterStage]           = useState('all');
  const [sortField, setSortField]               = useState('startTime');
  const [sortDir, setSortDir]                   = useState('asc');
  const [tick, setTick]                         = useState(0);
  const currentRole = getCurrentRole();
  const canCloseWorkOrder = hasPermission(currentRole, PERMISSIONS.CLOSE_WORK_ORDERS);
  const canVoidWorkOrder = hasPermission(currentRole, PERMISSIONS.VOID_WORK_ORDERS);

  // ── Fetch customer check-ins (not in store) ───────────────────────────────
  useEffect(() => {
    async function fetchCheckIns() {
      setCheckInsLoading(true);
      try {
        const res = await client.graphql({
          query: queries.listCustomerCheckIns,
          variables: { limit: 1000 },
        });
        const checkIns = res.data.listCustomerCheckIns.items.filter(
          c => !c.status || c.status !== 'processed'
        );
        setCustomerCheckIns(checkIns);
        if (onCustomerCheckInsUpdate) onCustomerCheckInsUpdate(checkIns);
      } catch (e) {
        console.error('ROListView fetchCheckIns:', e);
      } finally {
        setCheckInsLoading(false);
      }
    }
    fetchCheckIns();
  }, []);

  // ── Customer check-in subscription (not in store) ─────────────────────────
  useEffect(() => {
    const sub = client.graphql({ query: subscriptions.onCreateCustomerCheckIn }).subscribe({
      next: ({ data }) => {
        const c = data.onCreateCustomerCheckIn;
        if (!c.status || c.status !== 'processed') {
          setCustomerCheckIns(prev => {
            const updated = [...prev, c];
            if (onCustomerCheckInsUpdate) onCustomerCheckInsUpdate(updated);
            return updated;
          });
        }
      },
    });
    return () => sub.unsubscribe();
  }, [onCustomerCheckInsUpdate]);

  // ── Periodic tick to keep time health fresh ───────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // ── Emit boardState for RedBook and other App.js consumers ───────────────
  useEffect(() => {
    if (onBoardStateChange) onBoardStateChange(groupJobsByStatus(activeTrucks));
  }, [activeTrucks, onBoardStateChange]);

  // ── Close context menu on outside click ──────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (contextMenu.show && !e.target.closest('.context-menu')) {
        setContextMenu({ show: false, x: 0, y: 0 });
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [contextMenu.show]);

  // ── Filtered + sorted truck list ──────────────────────────────────────────
  const visible = useMemo(() => {
    let list = [...activeTrucks];
    if (filterStage !== 'all') {
      const stage = PIPELINE.find(s => s.id === filterStage);
      if (stage) list = list.filter(t => stage.statuses.includes(t.status));
    }
    list.sort((a, b) => {
      const av = a[sortField] || 0;
      const bv = b[sortField] || 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [activeTrucks, filterStage, sortField, sortDir, tick]);

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  // ── Mutation handlers ─────────────────────────────────────────────────────
  async function handleUpdateTechs(techNumbers) {
    if (!selectedTruck) return;
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: selectedTruck.id, techNumbers } },
      });
      setIsAssignTechOpen(false);
    } catch (e) { console.error(e); }
  }

  async function handleAddNote(note) {
    if (!selectedTruck || !note) return;
    const now = nowSeconds();
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: {
          input: {
            id: selectedTruck.id,
            notes: [...(selectedTruck.notes || []), `,${note}`],
            lastNote: now,
            lastStatusChange: now,
            warning: false,
            expired: false,
            critical: false,
          },
        },
      });
      recordTruckEvent(selectedTruck, EVENT_TYPES.NOTE_ADDED, {
        timestamp: now,
        note,
      });
      setIsNoteOpen(false);
    } catch (e) { console.error(e); }
  }

  async function handleEditTruck(updatedData) {
    if (!selectedTruck) return;
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: selectedTruck.id, ...updatedData } },
      });
      setIsEditOpen(false);
    } catch (e) {
      console.error(e);
      alert('Failed to update truck.');
    }
  }

  function handleCompleteTruck() {
    if (!canCloseWorkOrder) return;
    if (!selectedTruck) return;
    setContextMenu({ show: false, x: 0, y: 0 });
    setConfirmModal({
      isOpen: true,
      message: 'Complete Truck',
      requireLaborHours: true,
      onConfirm: async (hours) => {
        hours = Number(hours);
        if (!Number.isFinite(hours) || hours <= 0) {
          alert('Enter valid labor hours (e.g. 5.5)');
          return;
        }
        try {
          const now = nowSeconds();
          const statusHistory = (selectedTruck.statusHistory || [])
            .filter(entry => entry?.status && entry?.timeEntered)
            .map(entry => ({ status: entry.status, timeEntered: entry.timeEntered }));
          const newHistory = [...statusHistory, { status: 'completed', timeEntered: now }];
          const serviceSummary = createServiceSummaryJson({ ...selectedTruck, status: 'completed', completionTime: now, lastStatusChange: now, statusHistory: newHistory });
          // Optimistic update: status change causes it to filter out of activeTrucks immediately
          localUpdateTruck(selectedTruck.id, { status: 'completed', completionTime: now, lastStatusChange: now, statusHistory: newHistory, serviceSummary });
          await client.graphql({
            query: mutations.updateTruck,
            variables: { input: { id: selectedTruck.id, status: 'completed', completionTime: now, lastStatusChange: now, statusHistory: newHistory, serviceSummary } },
          });
          recordTruckEvent(selectedTruck, EVENT_TYPES.COMPLETED, {
            timestamp: now,
            previousStatus: selectedTruck.status,
            newStatus: 'completed',
            metadata: { laborHours: hours },
          });
          setConfirmModal({ isOpen: false });
        } catch (e) {
          console.error(e);
          alert('Failed to complete truck.');
          setConfirmModal({ isOpen: false });
        }
      },
      onCancel: () => setConfirmModal({ isOpen: false }),
    });
  }

  function handleVoidTruck() {
    if (!canVoidWorkOrder) return;
    if (!selectedTruck) return;
    setContextMenu({ show: false, x: 0, y: 0 });
    setConfirmModal({
      isOpen: true,
      message: 'Are you sure you want to void this repair? This will remove it from the board.',
      onConfirm: async () => {
        try {
          const now = nowSeconds();
          const statusHistory = (selectedTruck.statusHistory || [])
            .filter(entry => entry?.status && entry?.timeEntered)
            .map(entry => ({ status: entry.status, timeEntered: entry.timeEntered }));
          const newHistory = [...statusHistory, { status: 'voided', timeEntered: now }];
          const serviceSummary = createServiceSummaryJson({ ...selectedTruck, status: 'voided', completionTime: now, lastStatusChange: now, statusHistory: newHistory });
          // Optimistic update: status change causes it to filter out of activeTrucks immediately
          localUpdateTruck(selectedTruck.id, { status: 'voided', completionTime: now, lastStatusChange: now, statusHistory: newHistory, serviceSummary });
          await client.graphql({
            query: mutations.updateTruck,
            variables: { input: { id: selectedTruck.id, status: 'voided', completionTime: now, lastStatusChange: now, statusHistory: newHistory, serviceSummary } },
          });
          recordTruckEvent(selectedTruck, EVENT_TYPES.VOIDED, {
            timestamp: now,
            previousStatus: selectedTruck.status,
            newStatus: 'voided',
          });
          setConfirmModal({ isOpen: false });
        } catch (e) {
          console.error(e);
          alert('Failed to void truck.');
          setConfirmModal({ isOpen: false });
        }
      },
      onCancel: () => setConfirmModal({ isOpen: false }),
    });
  }

  async function handleCustomerProcessed(checkInId) {
    setCustomerCheckIns(prev => {
      const filtered = prev.filter(c => c.id !== checkInId);
      if (onCustomerCheckInsUpdate) onCustomerCheckInsUpdate(filtered);
      return filtered;
    });
    try {
      await client.graphql({
        query: mutations.updateCustomerCheckIn,
        variables: { input: { id: checkInId, status: 'processed' } },
      });
    } catch (e) {
      console.warn('Could not mark check-in processed:', e.message);
    }
  }

  if (storeLoading && checkInsLoading) {
    return <div className="ro-loading">Loading jobs…</div>;
  }

  return (
    <div className="ro-list-view">
      {/* Page header */}
      <div className="ro-page-header">
        <div className="ro-page-title-group">
          <h1 className="ro-page-title">Trucks</h1>
          <span className="ro-page-count">{visible.length} active</span>
        </div>
        <div className="ro-page-actions">
          {/* View toggle: list (active) ↔ board */}
          <div className="fv-view-toggle">
            <button className="fv-view-btn fv-view-btn--active" title="List view">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6"  x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
            <button className="fv-view-btn" title="Board view" onClick={() => setActiveView('flow')}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3"  width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            </button>
          </div>
          <FullscreenBtn />
          {onAddTruck && (
            <button className="ro-new-btn" onClick={onAddTruck}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New Truck
            </button>
          )}
        </div>
      </div>

      {/* Toolbar: stage filters */}
      <div className="ro-toolbar">
        <div className="ro-filters">
          <button
            className={`ro-filter-btn ${filterStage === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStage('all')}
          >
            All
          </button>
          {PIPELINE.map(s => (
            <button
              key={s.id}
              className={`ro-filter-btn ${filterStage === s.id ? 'active' : ''}`}
              onClick={() => setFilterStage(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* RO Table */}
      <div className="ro-table-wrap">
        <table className="ro-table">
          <thead>
            <tr>
              <th className="col-ro th-sortable" onClick={() => toggleSort('repairOrder')}>
                RO #{sortField === 'repairOrder' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
              <th className="col-customer">Customer</th>
              <th className="col-asset">Asset</th>
              <th className="col-status">Status</th>
              <th className="col-pipeline">Stage</th>
              <th className="col-health th-sortable" onClick={() => toggleSort('startTime')}>
                Time Health{sortField === 'startTime' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
              <th className="col-updated th-sortable" onClick={() => toggleSort('lastStatusChange')}>
                Updated{sortField === 'lastStatusChange' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={8} className="ro-empty">No active jobs.</td>
              </tr>
            )}
            {visible.map(truck => {
              const health    = getJobHealth(truck);
              const idle      = domainIsIdle(truck);
              const stageAge  = getStageAge(truck);
              const si        = getPipelineIndex(truck.status);
              const now       = Date.now();
              const elapsed      = getJobElapsedMs(truck, now);
              const stageElapsed = now - ((truck.lastStatusChange || 0) * 1000 || now);
              const techs = truck.techNumbers?.length
                ? truck.techNumbers
                : truck.techNumber ? [truck.techNumber] : [];
              const updated = fmtUpdated(truck.lastStatusChange);

              return (
                <tr
                  key={truck.id}
                  className={`ro-row health-${health} age-${stageAge.level}${idle ? ' ro-idle' : ''}${truck.warranty ? ' is-warranty' : ''}`}
                  onClick={() => setSelectedTruckId(truck.id)}
                  onContextMenu={e => {
                    e.preventDefault();
                    setSelectedTruck(truck);
                    setContextMenu({ show: true, x: e.clientX, y: e.clientY });
                  }}
                >
                  {/* RO Number (blue link look, warranty badge inline) */}
                  <td className="col-ro">
                    <span className="ro-num">{truck.repairOrder}</span>
                    {truck.warranty && <span className="warranty-w" title="Warranty">W</span>}
                    {idle && (
                      <span className="idle-flag" title={`Idle ${domainFmtDur(stageElapsed)} in this stage`}>⚑</span>
                    )}
                  </td>

                  {/* Customer */}
                  <td className="col-customer">
                    <div className="customer-name">{truck.companyName || '—'}</div>
                    {techs.length > 0 && (
                      <div className="customer-techs">
                        Tech{techs.length > 1 ? 's' : ''}: {techs.join(', ')}
                      </div>
                    )}
                  </td>

                  {/* Asset */}
                  <td className="col-asset">
                    <div className="asset-vehicle">
                      {[truck.year, truck.make, truck.model].filter(Boolean).join(' ') || truck.truckNumber || '—'}
                    </div>
                    <div className="asset-serial">
                      {[truck.truckNumber && `Unit ${truck.truckNumber}`, truck.vin && `VIN ${truck.vin.slice(-8)}`]
                        .filter(Boolean).join(' · ') || truck.complaint?.slice(0, 40) || ''}
                    </div>
                  </td>

                  {/* Status pill (variant per status) */}
                  <td className="col-status">
                    <span className={`status-pill status-pill--${PILL_VARIANTS[truck.status] || 'checkedin'}`}>
                      {getStageLabel(truck.status)}
                    </span>
                  </td>

                  {/* Stage mini-dots */}
                  <td className="col-pipeline">
                    <Pipeline stageIndex={si} />
                  </td>

                  {/* Time Health */}
                  <td className="col-health">
                    <div className="health-cell">
                      <span className={`health-dot health-${health}`} />
                      <div className="health-info">
                        <div className="health-total">{domainFmtDur(elapsed)} total</div>
                        <div className={`health-stage stage-age-${stageAge.level}`}>
                          {domainFmtDur(stageAge.stageMs)} here
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Updated */}
                  <td className="col-updated">
                    <div className="updated-date">{updated.date}</div>
                    <div className="updated-time">{updated.time}</div>
                  </td>

                  {/* Kebab actions */}
                  <td className="col-actions">
                    <button
                      className="kebab-btn"
                      title="Actions"
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedTruck(truck);
                        setContextMenu({ show: true, x: e.clientX, y: e.clientY });
                      }}
                    >
                      ⋮
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Context Menu */}
      <SmartContextMenu
        show={contextMenu.show}
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={() => setContextMenu({ show: false, x: 0, y: 0 })}
      >
        <div className="context-menu-item" onClick={() => {
          setIsAssignTechOpen(true);
          setContextMenu({ show: false, x: 0, y: 0 });
        }}>Assign Tech</div>
        <div className="context-menu-item" onClick={() => {
          setIsNoteOpen(true);
          setContextMenu({ show: false, x: 0, y: 0 });
        }}>Add Note</div>
        <div className="context-menu-item" onClick={() => {
          setIsEditOpen(true);
          setContextMenu({ show: false, x: 0, y: 0 });
        }}>Edit Truck</div>
        {canCloseWorkOrder && (
          <div className="context-menu-item" style={{ color: '#4CAF50' }} onClick={handleCompleteTruck}>
            Complete Truck
          </div>
        )}
        {canVoidWorkOrder && (
          <div className="context-menu-item" style={{ color: '#ff4444' }} onClick={handleVoidTruck}>
            Void Truck
          </div>
        )}
      </SmartContextMenu>

      {/* Modals */}
      {isAssignTechOpen && selectedTruck && (
        <AssignTechModal
          onClose={() => setIsAssignTechOpen(false)}
          onSubmit={handleUpdateTechs}
          currentTechs={selectedTruck.techNumbers || [selectedTruck.techNumber].filter(Boolean)}
        />
      )}

      {isNoteOpen && selectedTruck && (
        <NoteModal
          onClose={() => setIsNoteOpen(false)}
          onSubmit={handleAddNote}
          currentNotes={selectedTruck.notes || []}
        />
      )}

      {isEditOpen && selectedTruck && (
        <NewROModal
          onClose={() => setIsEditOpen(false)}
          onSubmit={handleEditTruck}
          editMode={true}
          truckData={selectedTruck}
        />
      )}


      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel || (() => setConfirmModal({ isOpen: false }))}
        requireLaborHours={!!confirmModal.requireLaborHours}
      />
    </div>
  );
}

// Stage cell (mockup style): current stage label over a mini dot progress row
function Pipeline({ stageIndex }) {
  const current = PIPELINE[stageIndex];
  return (
    <div className="pipeline" title={current ? current.label : ''}>
      <div className="pipe-current-label">{current ? current.label : '—'}</div>
      <div className="pipe-dots">
        {PIPELINE.map((stage, i) => {
          const isCurrent = stageIndex === i;
          const isPast = stageIndex > i && stageIndex !== -1;
          return (
            <React.Fragment key={stage.id}>
              <div
                className={`pipe-node ${isCurrent ? 'node-current' : isPast ? 'node-past' : 'node-future'}`}
              />
              {i < PIPELINE.length - 1 && (
                <div className={`pipe-connector${isPast ? ' filled' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
